/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React, { Suspense, useMemo } from "react";
import { AuthProvider } from "react-oidc-context";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { UIProvider } from "@/shared/ui";
import { LoadingProvider } from "@/context/LoadingContext";
import { globalErrorContext } from "@/context/ErrorContext";
import { LoadingPage } from "@pages/other/LoadingPage";
import { getOidcConfiguration } from "@/context/authentication";
import { FF_MOCK_AUTH } from "@shared/utils/feature-flag";
import { MockAuthenticationProvider } from "@/context/authentication-mock";
import { ensureRoutableSigninState } from "@shared/utils/oidc-signin-state-repair";
import { getRealLocalStorage } from "@shared/utils/storage/localStorageWrapper";

import AuthenticationCallback from "@pages/authenticated/callback";

/**
 * Minimal shell for the authentication callback route.
 *
 * This shell intentionally excludes AntiBotGate and AuthGate because:
 * - The callback IS the authentication flow — it cannot depend on auth being resolved
 * - reCAPTCHA SDK readiness is irrelevant here — POST /user is exempt from anti-bot
 * - The callback page manages its own loading/error states internally
 */
export const CallbackShell: React.FC = () => {
  const { Provider: ErrorContextProvider } = globalErrorContext;

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
    []
  );

  return (
    <QueryClientProvider client={queryClient}>
      <UIProvider>
        <LoadingProvider>
          <ErrorContextProvider>
            <Suspense fallback={<LoadingPage />}>
              <CallbackAuthProvider>
                <AuthenticationCallback />
              </CallbackAuthProvider>
            </Suspense>
          </ErrorContextProvider>
        </LoadingProvider>
      </UIProvider>
    </QueryClientProvider>
  );
};

const CallbackAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (FF_MOCK_AUTH) {
    return <MockAuthenticationProvider>{children}</MockAuthenticationProvider>;
  }

  // Native sign-in fix: before react-oidc-context's AuthProvider mounts and
  // calls UserManager.signinCallback(), repair the pending OIDC signin-state so
  // its internal `request_type` is routable ("si:r"). On the Capacitor shell the
  // authorize step can round-trip through an external browser / deeplink hop and
  // leave the stored state without a valid request_type, which makes
  // signinCallback() throw "invalid response_type in state" and bounce the user
  // to the landing page. Repairing it routes the callback into
  // signinRedirectCallback, which still performs the full PKCE + state + nonce
  // validation — no security check is weakened. No-op on the web happy path.
  ensureRoutableSigninState(window.location.href, getRealLocalStorage());

  return (
    <AuthProvider
      {...getOidcConfiguration()}
      onSigninCallback={() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }}
      onSigninError={(error: Error) => {
        console.error('[OIDC] signinCallback error:', error);
      }}
    >
      {children}
    </AuthProvider>
  );
};
